/**
 * The set of language codes recognized by the app's translation system.
 * Mirrors SUPPORTED_LANGS in @paintscout/ui — defined here separately to avoid
 * a circular package dependency (@paintscout/util must not depend on @paintscout/ui).
 */
export type FormattingLang = 'en' | 'es' | 'fr'

/** Default language used when no lang is specified at a call site. */
export const DEFAULT_LANG: FormattingLang = 'en'

/**
 * Maps the app's language codes to BCP 47 locale tags understood by
 * Intl.NumberFormat.  Falls back to 'en-US' for any unrecognized code.
 *
 * Note: French (fr-FR) and Spanish (es-ES) use a thin non-breaking space
 * (\u202f) as the thousands separator and a comma as the decimal separator.
 * Downstream code that compares, splits, or regex-matches these formatted
 * strings must account for \u202f rather than a plain space.
 */
const LANG_TO_LOCALE: Record<FormattingLang, string> = {
  en: 'en-US',
  es: 'es-419',
  fr: 'fr-FR'
}

export function langToLocale(lang: FormattingLang | string): string {
  return (LANG_TO_LOCALE as Record<string, string>)[lang] ?? 'en-US'
}

/** Memoized Intl.NumberFormat instances keyed by "<locale>|<serialized-options>". */
const formatterCache = new Map<string, Intl.NumberFormat>()

function getFormatter(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = formatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    formatterCache.set(key, formatter)
  }
  return formatter
}

/**
 * Format a number using the locale that corresponds to the given app language
 * code.  Any additional Intl.NumberFormatOptions are forwarded verbatim, so
 * callers can control decimal places, style, etc.
 *
 * Intl.NumberFormat instances are cached so repeated calls with the same
 * locale + options do not reconstruct the formatter.
 *
 * Examples:
 *   formatNumber(12.5, { lang: 'en' })          → "12.5"
 *   formatNumber(12.5, { lang: 'fr' })          → "12,5"
 *   formatNumber(1234.5, { lang: 'fr' })        -> "1\u202f234,5"  (thin-NBSP thousands sep)
 *   formatNumber(50.1, { lang: 'es', minimumFractionDigits: 1 }) → "50,1"
 */
export function formatNumber(
  value: number,
  { lang = DEFAULT_LANG, ...numberFormatOptions }: { lang?: FormattingLang | string } & Intl.NumberFormatOptions = {}
): string {
  const locale = langToLocale(lang)
  return getFormatter(locale, numberFormatOptions).format(value)
}
